"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Somnath (e0232a65) phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (jyotirlinga — no opener shift). 1280×704.
// Every `start` is the EXACT word_timestamps_hindi time of the phrase's key word (verified).
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 42 phrase cards. Numerals stay LATIN (27, 10,000, 1026, 1951, 11); names/terms in Devanagari.
// out = start + ~3.4s, except #9 (दक्ष का श्राप) which is clipped to 149.8 so it clears before
// #10 (क्षय रोग) at 150.03 — two top-left holes cards on screen at once collide visually.
export const SOMNATH_PHRASES: Phrase[] = [
  // ── HOOK ──
  { start: 7.38,    out: 10.78,   lines: [[{ t: "महमूद", c: "y" }, { t: "ग़ज़नवी", c: "w", big: true }]] },
  { start: 14.14,   out: 17.54,   lines: [[{ t: "चूर", c: "r", big: true }, { t: "चूर", c: "r", big: true }]] },
  { start: 22.75,   out: 26.15,   lines: [[{ t: "खून", c: "w" }, { t: "खौल", c: "r", big: true }, { t: "उठेगा", c: "r" }]] },
  { start: 26.20,   out: 29.60,   lines: [[{ t: "धरती", small: true }, { t: "का", small: true }], [{ t: "पहला", c: "y" }, { t: "शिवलिंग", c: "w", big: true }]] },
  { start: 48.71,   out: 52.11,   lines: [[{ t: "हवा", small: true }, { t: "में", small: true }], [{ t: "तैरता", c: "y", big: true }, { t: "शिवलिंग", c: "w" }]] },
  { start: 62.93,   out: 66.33,   lines: [[{ t: "सात", c: "r", big: true }, { t: "बार", c: "w" }], [{ t: "तोड़ा", c: "r" }, { t: "गया", c: "y" }]] },

  // ── CHANDRA / DAKSHA CURSE ──
  { start: 117.19,  out: 120.59,  lines: [[{ t: "27", c: "w", big: true }, { t: "बेटियाँ", c: "y" }]] },
  { start: 127.31,  out: 130.71,  lines: [[{ t: "सिर्फ़", small: true }], [{ t: "रोहिणी", c: "y", big: true }]] },
  { start: 147.89,  out: 149.80,  lines: [[{ t: "दक्ष", c: "w" }, { t: "का", small: true }], [{ t: "श्राप", c: "r", big: true }]] },
  { start: 150.03,  out: 153.43,  lines: [[{ t: "क्षय", c: "r", big: true }, { t: "रोग", c: "w" }]] },
  { start: 159.94,  out: 163.34,  lines: [[{ t: "तेज", c: "y" }, { t: "घटने", c: "r", big: true }, { t: "लगा", c: "r" }]] },
  { start: 173.94,  out: 177.34,  lines: [[{ t: "सृष्टि", small: true }, { t: "में", small: true }], [{ t: "हाहाकार", c: "r", big: true }]] },
  { start: 183.20,  out: 186.60,  lines: [[{ t: "महादेव", c: "y" }, { t: "की", small: true }], [{ t: "शरण", c: "w", big: true }]] },
  { start: 195.64,  out: 199.04,  lines: [[{ t: "घोर", c: "y" }, { t: "तपस्या", c: "w", big: true }]] },
  { start: 223.51,  out: 226.91,  lines: [[{ t: "15", c: "r", big: true }, { t: "दिन", c: "w" }, { t: "घटेगा", small: true }], [{ t: "15", c: "y", big: true }, { t: "दिन", c: "w" }, { t: "बढ़ेगा", small: true }]] },
  { start: 237.14,  out: 240.54,  lines: [[{ t: "अमावस्या", c: "y", big: true }], [{ t: "का", small: true }, { t: "राज़", c: "w" }]] },
  { start: 254.01,  out: 257.41,  lines: [[{ t: "सोम", c: "y" }, { t: "के", small: true }, { t: "नाथ", c: "w", big: true }]] },
  { start: 262.30,  out: 265.70,  lines: [[{ t: "धरती", small: true }, { t: "का", small: true }], [{ t: "पहला", c: "y" }, { t: "ज्योतिर्लिंग", c: "w", big: true }]] },
  { start: 310.23,  out: 313.63,  lines: [[{ t: "सौराष्ट्रे", c: "y" }], [{ t: "सोमनाथं", c: "w", big: true }, { t: "च", c: "y" }]] },

  // ── PRABHAS / KRISHNA ──
  { start: 338.38,  out: 341.78,  lines: [[{ t: "श्रीकृष्ण", c: "y" }, { t: "ने", small: true }], [{ t: "देह", c: "r", big: true }, { t: "त्यागी", c: "r" }]] },
  { start: 360.43,  out: 363.83,  lines: [[{ t: "भालका", c: "y" }, { t: "तीर्थ", c: "w", big: true }]] },

  // ── WEALTH / FLOATING SHIVLING ──
  { start: 401.76,  out: 405.16,  lines: [[{ t: "अकूत", c: "y" }, { t: "दौलत", c: "w", big: true }]] },
  { start: 418.28,  out: 421.68,  lines: [[{ t: "10,000", c: "w", big: true }, { t: "गाँव", c: "y" }]] },
  { start: 459.28,  out: 462.68,  lines: [[{ t: "चुंबकीय", c: "y" }], [{ t: "संतुलन", c: "w", big: true }]] },

  // ── MAHMUD OF GHAZNI ──
  { start: 489.82,  out: 493.22,  lines: [[{ t: "1026", c: "r", big: true }]] },
  { start: 529.72,  out: 533.12,  lines: [[{ t: "हज़ारों", small: true }, { t: "ने", small: true }], [{ t: "प्राण", c: "r", big: true }, { t: "दिए", c: "r" }]] },
  { start: 555.90,  out: 559.30,  lines: [[{ t: "मूर्ति", c: "w" }, { t: "तोड़ने", c: "r", big: true }], [{ t: "वाला", c: "r" }]] },
  { start: 568.61,  out: 572.01,  lines: [[{ t: "चुंबक", c: "y" }], [{ t: "उखाड़", c: "r", big: true }, { t: "फेंके", c: "r" }]] },
  { start: 598.58,  out: 601.98,  lines: [[{ t: "मस्जिद", small: true }, { t: "की", small: true }], [{ t: "सीढ़ियों", c: "r", big: true }, { t: "पर", c: "w" }]] },
  { start: 656.08,  out: 659.48,  lines: [[{ t: "रेगिस्तान", c: "y" }, { t: "में", small: true }], [{ t: "तड़प", c: "r" }, { t: "कर", small: true }, { t: "मरे", c: "r", big: true }]] },

  // ── REBUILDS ──
  { start: 761.53,  out: 764.93,  lines: [[{ t: "अहिल्याबाई", c: "y" }], [{ t: "होल्कर", c: "w", big: true }]] },
  { start: 798.72,  out: 802.12,  lines: [[{ t: "सातों", c: "r", big: true }, { t: "बार", c: "w" }], [{ t: "फिर", c: "y" }, { t: "खड़ा", c: "w" }]] },
  { start: 865.25,  out: 868.65,  lines: [[{ t: "नक़ली", c: "r", big: true }, { t: "दरवाज़े", c: "w" }]] },

  // ── BAAN STAMBH MYSTERY ──
  { start: 881.57,  out: 884.97,  lines: [[{ t: "बाण", c: "y" }, { t: "स्तंभ", c: "w", big: true }]] },
  { start: 900.25,  out: 903.65,  lines: [[{ t: "दक्षिणी", c: "y" }], [{ t: "ध्रुव", c: "w", big: true }, { t: "तक", small: true }]] },
  { start: 912.44,  out: 915.84,  lines: [[{ t: "सीधे", small: true }], [{ t: "अंटार्कटिका", c: "w", big: true }]] },

  // ── 1947 → 1951 ──
  { start: 985.51,  out: 988.91,  lines: [[{ t: "सरदार", c: "y" }, { t: "पटेल", c: "y" }], [{ t: "का", small: true }, { t: "प्रण", c: "w", big: true }]] },
  { start: 1023.62, out: 1027.02, lines: [[{ t: "नेहरू", c: "w" }], [{ t: "असहज", c: "r", big: true }]] },
  { start: 1072.25, out: 1075.65, lines: [[{ t: "11", c: "y" }, { t: "मई", c: "y" }], [{ t: "1951", c: "w", big: true }]] },
  { start: 1092.32, out: 1095.72, lines: [[{ t: "विनाश", small: true }, { t: "पर", small: true }], [{ t: "निर्माण", c: "y", big: true }, { t: "भारी", c: "w" }]] },

  // ── THE MAKERS / CLOSE ──
  { start: 1130.84, out: 1134.24, lines: [[{ t: "सोमपुरा", c: "y" }], [{ t: "शिल्पी", c: "w", big: true }]] },
  { start: 1215.48, out: 1218.88, lines: [[{ t: "पत्थर", c: "w" }, { t: "मिटे", small: true }], [{ t: "विश्वास", c: "y", big: true }, { t: "नहीं", c: "r" }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip — two lines, yellow "Watch at 1.2x" + smaller white
// "for best experience". NO holes mask. 0→5s only.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op,
                  display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x
      </span>
      <span style={{ color: WHITE, fontSize: Math.round(SMALL_FS * 0.7), fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        for best experience
      </span>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser — SMALL_FS, yellow text, WHITE cycling dots. NO holes mask.
// Runs over the opening hook, 3→90s (Yash, 2026-07-11).
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.5, out - 0.6, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const n = Math.floor((t * 1.5) % 4); // 0,1,2,3 dots
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        आगे देखिए<span style={{ display: "inline-block", width: "1.4em", textAlign: "left", color: WHITE }}>{".".repeat(n)}</span>
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (teaser, watch tip, LS pills).
export const SomnathOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = SOMNATH_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* आगे देखिए teaser (bottom-left, cycling dots) over the opening hook */}
      <AageDekhiye fps={fps} start={3.0} out={90.0} />

      {/* viewing tip (bottom-right) */}
      <WatchTag fps={fps} start={0.0} out={5.0} />

      {/* Like+Subscribe — LS2 (bell) @ "सब्सक्राइब" 91.56s · LS5 (no bell) @ outro "सब्सक्राइब" 1304.18s */}
      <Sequence from={S(91.56)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1304.18)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
